import { ORDERS } from 'constants/permissions';
import { usePermission } from 'hooks/usePermission';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import BadgeLabel from 'components/BadgeLabel';
import NewTabLink from 'components/NewTabLink';
import PaginateTable from 'components/PaginateTable';
import { CopyOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import dayjs from 'dayjs';
import {
  CANCELLED_BY_RESTAURANT,
  COMPLETED,
  DELIVERED,
  IN_PROGRESS,
  NEEDS_APPROVAL,
  NEW,
  ON_THE_WAY,
  OrderStatusLabel,
  PAYMENT_SCHEDULED,
  READY,
  REJECTED_BY_RESTAURANT,
  STRANDED,
} from 'constants/status';
import { priceTransformer } from 'utils';
import { StyledTable } from './styles';

function Table({
  categoryTitle,
  loading,
  items,
  pagination,
  resetPaging,
  refetch,
  ...props
}) {
  const navigate = useNavigate();
  const hasOrderPermission = usePermission(ORDERS);
  const base = (process.env.REACT_APP_BASENAME || '').replace(/\/$/, '');

  const handleStatus = (state) => {
    const statusName = OrderStatusLabel[state] ?? `Status ${state}`;
    const statusClassName = {
      [NEW]: 'new',
      [IN_PROGRESS]: 'in-progress',
      [CANCELLED_BY_RESTAURANT]: 'cancelled',
      [READY]: 'ready',
      [ON_THE_WAY]: 'on-the-way',
      [COMPLETED]: 'completed',
      [STRANDED]: 'cancelled',
      [REJECTED_BY_RESTAURANT]: 'cancelled',
      [NEEDS_APPROVAL]: 'in-progress',
      [DELIVERED]: 'completed',
      [PAYMENT_SCHEDULED]: 'in-progress',
    }[state] ?? 'in-progress';

    return (
      <BadgeLabel
        label={statusName}
        classes={`status status--${statusClassName}`}
      />
    );
  };

  const handleFormatDate = (d) => (d ? dayjs(d).format('MMM D, hh:mm A') : '-');

  const handleCopyOrder = (e, row) => {
    e.stopPropagation();
    if (hasOrderPermission && row?.id) {
      navigate(`/console/orders/direct-entry?clone=${row.id}`);
    }
  };

  return (
    <StyledTable>
      <PaginateTable
        loading={loading}
        headers={[
          { title: 'Order #', width: '100px' },
          { title: 'Status', width: '140px' },
          { title: 'Created Date', width: '140px' },
          { title: 'Due Date', width: '140px' },
          { title: 'Amount', width: '100px' },
          { title: 'Merchant', width: 'auto' },
          { title: '', width: '48px' },
        ]}
        dataArray={items}
        rowCreators={[
          (row) => (
            <div className="strong">{row.number ?? row.id ?? '-'}</div>
          ),
          (row) => <div>{handleStatus(row.state)}</div>,
          (row) => (
            <div>{handleFormatDate(row.created_at ?? row.state_modified)}</div>
          ),
          (row) => (
            <div>
              {handleFormatDate(row.scheduled_for ?? row.requested_pickup_time)}
            </div>
          ),
          (row) => (
            <div className="strong">{priceTransformer(row.value)}</div>
          ),
          (row) => (
            <div className="strong">
              {row.restaurant_id ? (
                <NewTabLink
                  label={row.restaurant}
                  link={`/console/restaurants/${row.restaurant_id}`}
                />
              ) : (
                row.restaurant ?? '-'
              )}
            </div>
          ),
          (row) => (
            <Tooltip title="Copy Order">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={(e) => handleCopyOrder(e, row)}
                aria-label="Copy Order"
              />
            </Tooltip>
          ),
        ]}
        noRecord="There are no records to display."
        onClick={(record) => {
          if (hasOrderPermission && record?.id) {
            const orderUrl = `${window.location.origin}${base}/console/orders/${record.id}`;
            window.open(orderUrl, '_blank', 'noopener,noreferrer');
          }
        }}
        newRowClasses="new-row"
        dataPaging={pagination}
      />
    </StyledTable>
  );
}

export default Table;
